import { Notifications, Notification } from '../components/NotificationList.types';
import { useEffect, useState, useCallback } from 'react';
import {
  fetchNotifications,
  subscribeToNewNotifications,
  subscribeToUpdatedNotifications,
  updateNotificationStatus
} from '../services/apiService';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';

export const useNotifications = (patientId: string, facilityId: string) => {
  const [notifications, setNotifications] = useState<Notification[] | undefined>(undefined); // 初期値は undefined
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true); // 初回ローディング状態

  // 通知を既読にする関数
  const markAsRead = async (notification: Notification) => {
    try {
      await updateNotificationStatus(notification.id);
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }

      console.error('Error updating notification:', error);
    }
  };

  const markAllAsRead = async (notificationsToMark: Notification[]) => {
    // 未読の通知だけをフィルタリングして並行して処理
    await Promise.all(
      notificationsToMark
        .filter((notification) => !notification.isRead)
        .map(async (notification) => {
          await markAsRead(notification);
        })
    );
  };

  // 初回通知の取得
  const loadInitialNotifications = useCallback(async () => {
    if (!patientId || !facilityId || !isInitialLoading) return;

    setIsLoading(true);
    try {
      const result = await fetchNotifications(`${patientId}#${facilityId}`, null);
      if (result) {
        const newNotifications: Notifications = result.items.filter((item) => item != null) as Notifications;
        setNotifications(newNotifications.reverse()); // 最新順に整列
        setNextToken(result.nextToken ?? null);
        setHasMore(!!result.nextToken);
        const filteredNotifications = newNotifications.filter((notification: any) => notification !== null);
        await markAllAsRead(filteredNotifications);
      } else {
        setNotifications([]); // 初回フェッチ結果が空の場合
        setHasMore(false);
      }
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }
      console.error('Error loading initial notifications:', error);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [patientId, facilityId, isInitialLoading]);

  // 次回通知の取得
  const loadMoreNotifications = useCallback(async () => {
    if (!patientId || !facilityId || !hasMore || isLoading) return;

    setIsLoading(true);
    try {
      const result = await fetchNotifications(`${patientId}#${facilityId}`, nextToken);
      if (result) {
        const newNotifications = result.items.filter((item) => item != null);
        // setNotifications((prev) => [...(prev || []), ...newNotifications.reverse()]);
        const reversedNotifications: any = newNotifications.reverse();
        setNotifications((prev) => [...reversedNotifications, ...(prev || [])]);
        setNextToken(result.nextToken ?? null);
        setHasMore(!!result.nextToken);
        const filteredNotifications = newNotifications.filter(
          (notification): notification is Notification => notification !== null
        );
        await markAllAsRead(filteredNotifications);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
        Sentry.captureException(error);
      }
      console.error('Error loading more notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [patientId, facilityId, hasMore, isLoading, nextToken]);

  // 初期読み込み
  useEffect(() => {
    loadInitialNotifications();
  }, [loadInitialNotifications]);

  // 通知のサブスクリプション
  useEffect(() => {
    if (!patientId || !facilityId) return;

    const patientFacilityId = `${patientId}#${facilityId}`;
    // 新規通知のサブスクリプション
    const newNotificationSubscription = subscribeToNewNotifications(
      patientFacilityId,
      () => {
        loadInitialNotifications();
      },
      (error) => {
        console.error('Subscription error', error);
      }
    );

    const updatedNotificationSubscription = subscribeToUpdatedNotifications(
      patientFacilityId,
      (data) => {
        if (data && data.onUpdateNotification) {
          const updatedNotification: Notification = {
            ...data.onUpdateNotification
          };
          setNotifications((prev) =>
            (prev || []).map((n) => (n.id === updatedNotification.id ? updatedNotification : n))
          );
        }
      },
      (error) => {
        console.error('Subscription update error', error);
      }
    );

    return () => {
      newNotificationSubscription.unsubscribe();
      updatedNotificationSubscription.unsubscribe();
    };
  }, [patientId, facilityId]);

  return {
    notifications,
    hasMore,
    isLoading,
    isInitialLoading,
    loadMoreNotifications,
    markAsRead
  };
};
